import { useDataStore } from "@store/user_org";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";

const TIERS = [
  { name: "Starter", limit: 5, price: "Inclus" },
  { name: "Pro", limit: 25, price: "9/mois" },
  { name: "Business", limit: 100, price: "29/mois" },
  { name: "Enterprise", limit: 500, price: "Sur devis" },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

function getActiveTier(usedGb: number) {
  for (const tier of TIERS) {
    if (usedGb < tier.limit) return tier;
  }
  return TIERS[TIERS.length - 1];
}

export function Storage() {
  const orgData = useDataStore((s) => s.orgData);
  const usedBytes = orgData?.storage_used_bytes || 0;
  const usedGb = usedBytes / (1024 * 1024 * 1024);
  const activeTier = getActiveTier(usedGb);
  const limitBytes = activeTier.limit * 1024 * 1024 * 1024;
  const percentage = Math.min(100, (usedBytes / limitBytes) * 100);

  const barColor = percentage > 90 ? "#DC2626" : percentage > 70 ? "#D97706" : "#3B82F6";

  return (
    <div className="flex flex-col h-fit w-full max-w-500 pb-96 mt-10 mx-4 lg:ml-10">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col">
          <Text1>Stockage</Text1>
          <Text3>Espace de stockage utilise par votre organisation</Text3>
        </div>

        {/* Usage card */}
        <div className="flex flex-col gap-4 bg-white p-5 rounded-lg border-1">
          <div className="flex flex-row justify-between items-end">
            <div className="flex flex-col">
              <Text2>Utilisation</Text2>
              <span style={{ fontFamily: "Poppins", fontSize: 28, fontWeight: 600, color: "#171717", lineHeight: "36px" }}>
                {formatBytes(usedBytes)}
              </span>
            </div>
            <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#737373" }}>
              / {activeTier.limit} Go
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ width: "100%", height: 8, background: "#F5F5F5", borderRadius: 4, overflow: "hidden" }}>
            <div
              style={{
                width: `${percentage}%`,
                height: "100%",
                background: barColor,
                borderRadius: 4,
                transition: "width 0.5s ease",
              }}
            />
          </div>

          <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A1A1A1" }}>
            {percentage.toFixed(1)}% utilise
          </span>
        </div>

        {/* Pricing tiers */}
        <div className="flex flex-col gap-3">
          <Text2>Forfaits stockage</Text2>
          <div className="flex flex-col gap-2">
            {TIERS.map((tier) => {
              const isActive = tier.name === activeTier.name;
              return (
                <div
                  key={tier.name}
                  className="flex flex-row items-center justify-between p-4 rounded-lg border-1"
                  style={{
                    background: isActive ? "#F0F7FF" : "#FFFFFF",
                    borderColor: isActive ? "#3B82F6" : "#E5E5E5",
                  }}
                >
                  <div className="flex flex-col">
                    <span style={{
                      fontFamily: "Poppins",
                      fontSize: 14,
                      fontWeight: 600,
                      color: isActive ? "#3B82F6" : "#171717",
                    }}>
                      {tier.name}
                    </span>
                    <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#737373" }}>
                      Jusqu'a {tier.limit} Go
                    </span>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <span style={{
                      fontFamily: "Poppins",
                      fontSize: 14,
                      fontWeight: 500,
                      color: isActive ? "#3B82F6" : "#525252",
                    }}>
                      {tier.price}
                    </span>
                    {isActive && (
                      <span style={{
                        fontFamily: "Poppins",
                        fontSize: 11,
                        fontWeight: 500,
                        color: "#3B82F6",
                        background: "#DBEAFE",
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}>
                        Actuel
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info */}
        <div className="flex flex-col gap-1 p-4 rounded-lg" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
          <span style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 500, color: "#92400E" }}>
            Compression automatique
          </span>
          <span style={{ fontFamily: "Poppins", fontSize: 12, color: "#A16207" }}>
            Les images sont automatiquement compressees avant l'envoi pour optimiser votre espace de stockage (max 1920px, JPEG).
          </span>
        </div>
      </div>
    </div>
  );
}
